import { useEffect, useRef } from "react";
import styled from "styled-components";
import { tokens } from "../../../styles";
import { buildScene, W, H } from "./scene";
import { paintFrame } from "./paint";

/*
  Bonsai: a bonsai tree made of dots, in the same style as the loss landscapes.
  The pot appears, the trunk twists upward, limbs reach out and each foliage pad
  blooms from its centre. Once grown, the outer foliage slowly changes size and
  brightness while the view swings back and forth like the homepage figure. It
  pauses while off screen and shows the finished tree, still, for visitors who
  prefer reduced motion.
*/

const Figure = styled.figure`
  margin: 0;
`;
const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: ${W} / ${H};
`;

export default function Bonsai({ ink = tokens.blue, className }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return undefined;
    const scene = buildScene();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(W * dpr);
    cv.height = Math.round(H * dpr);
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      paintFrame(ctx, scene, scene.end + 1, ink, true);
      return undefined;
    }

    // The clock only advances while the figure is on screen.
    let raf = 0, elapsed = 0, last = 0, visible = true;
    const loop = (now) => {
      if (last) elapsed += Math.min(0.1, (now - last) / 1000);
      last = now;
      paintFrame(ctx, scene, elapsed, ink, false);
      raf = visible ? requestAnimationFrame(loop) : 0;
      if (!raf) last = 0;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !raf) raf = requestAnimationFrame(loop);
    });
    io.observe(cv);
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [ink]);

  return (
    <Figure className={className}>
      <Canvas
        ref={canvasRef}
        width={W}
        height={H}
        role="img"
        aria-label="A bonsai tree made of dots, growing in a pot"
      />
    </Figure>
  );
}